import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NAV_CATEGORIES } from "../lib/categories";
import { Search, Menu, X, Youtube, Instagram, Mail, Bell, BellRing } from "lucide-react";
import { SOCIAL, MAILTO, EMAIL_LABEL } from "../lib/brand";
import { pushSupported, currentPermission, enableBreakingAlerts, disableBreakingAlerts } from "../lib/push";
import { toast } from "sonner";

function BreakingAlertsToggle() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (pushSupported()) setOn(currentPermission() === "granted"); }, []);
  if (!pushSupported()) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disableBreakingAlerts(); setOn(false); toast.success("Breaking alerts turned off"); }
      else {
        const r = await enableBreakingAlerts();
        if (r.enabled) { setOn(true); toast.success("Breaking news alerts enabled"); }
        else toast.error("Allow notifications in your browser to get breaking alerts.");
      }
    } catch { toast.error("Could not update breaking alerts."); }
    setBusy(false);
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid="breaking-alerts-toggle"
      aria-label={on ? "Disable breaking news alerts" : "Enable breaking news alerts"}
      className="flex items-center gap-1 hover:text-r24-red font-semibold uc tracking-wide disabled:opacity-50">
      {on ? <BellRing size={14} /> : <Bell size={14} />}<span className="hidden sm:inline">{on ? "Alerts On" : "Breaking Alerts"}</span>
    </button>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState("");
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setShowSearch(false); setOpen(false); setQ(""); }
  };

  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <header className="sticky top-0 z-50 bg-white border-b-2 border-black">
      {/* top strip */}
      <div className="bg-r24-black text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-8">
          <span className="font-mono2 hidden sm:block text-gray-300" data-testid="header-date">{dateStr}</span>
          <span className="font-mono2 sm:hidden text-gray-300">{now.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
          <div className="flex items-center gap-3">
            <BreakingAlertsToggle />
            <span className="text-gray-600">|</span>
            <Link to="/submit-tip" className="hover:text-r24-red font-semibold uc tracking-wide" data-testid="header-tip-link">Submit News Tip</Link>
            <span className="text-gray-600">|</span>
            <a href={SOCIAL.youtube.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.youtube.label} data-testid="header-youtube" className="hover:text-r24-red"><Youtube size={15} /></a>
            <a href={SOCIAL.instagram.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.instagram.label} data-testid="header-instagram" className="hover:text-r24-red"><Instagram size={14} /></a>
            <a href={MAILTO} aria-label={EMAIL_LABEL} data-testid="header-email" className="hover:text-r24-red"><Mail size={14} /></a>
          </div>
        </div>
      </div>

      {/* main bar */}
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-[68px] gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
          <img src="/brand/logo.jpg" alt="Rapid24News" className="h-11 w-11 rounded-full object-cover ring-2 ring-r24-red" />
          <div className="leading-none">
            <div className="font-head font-bold text-2xl tracking-tight">RAPID<span className="text-r24-red">24</span>NEWS</div>
            <div className="text-[9px] uc tracking-[0.25em] text-gray-500 font-semibold">Fast • Trusted • Fearless</div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <button onClick={() => setShowSearch((s) => !s)} aria-label="Search" data-testid="search-toggle-btn" className="p-2 hover:text-r24-red">
            <Search size={22} />
          </button>
          <Link to="/breaking" className="hidden md:inline-flex bg-r24-red text-white font-bold uc text-xs px-4 py-2 items-center gap-1 hover:bg-r24-reddark" data-testid="breaking-cta">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" /> Breaking
          </Link>
          <button onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn" className="p-2 lg:hidden hover:text-r24-red">
            <Menu size={24} />
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="border-t border-gray-200 bg-gray-50">
          <form onSubmit={doSearch} className="max-w-7xl mx-auto px-4 py-3 flex gap-2" data-testid="header-search-form">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="खोजें — Lucknow Police, UP, Crime, Cricket..." data-testid="header-search-input"
              className="flex-1 border border-gray-300 px-4 py-2 outline-none focus:border-r24-red font-body" />
            <button className="bg-r24-red text-white px-5 font-bold uc text-sm" data-testid="header-search-submit">Search</button>
          </form>
        </div>
      )}

      {/* desktop nav */}
      <nav className="hidden lg:block border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1 overflow-x-auto no-scrollbar h-11">
          <Link to="/" className="font-head font-semibold text-sm uc px-3 py-2 link-head whitespace-nowrap">Home</Link>
          {NAV_CATEGORIES.map((c) => (
            <Link key={c.slug} to={c.slug === "video" ? "/video" : `/category/${c.slug}`} data-testid={`nav-${c.slug}`}
              className="font-head font-semibold text-sm uc px-3 py-2 link-head whitespace-nowrap">
              {c.name}
            </Link>
          ))}
          <Link to="/archive" data-testid="nav-archive" className="font-head font-semibold text-sm uc px-3 py-2 link-head whitespace-nowrap">Archive</Link>
        </div>
      </nav>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="font-head font-bold text-xl">RAPID<span className="text-r24-red">24</span>NEWS</div>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X size={26} /></button>
            </div>
            <form onSubmit={doSearch} className="flex gap-2 mb-5">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search news..." data-testid="mobile-search-input"
                className="flex-1 border border-gray-300 px-3 py-2 outline-none focus:border-r24-red" />
              <button className="bg-r24-red text-white px-4 font-bold"><Search size={18} /></button>
            </form>
            <Link to="/breaking" onClick={() => setOpen(false)} className="block bg-r24-red text-white font-bold uc text-sm px-4 py-3 mb-3 text-center" data-testid="mobile-nav-breaking">🔴 Breaking News</Link>
            <div className="flex flex-col">
              {NAV_CATEGORIES.map((c) => (
                <Link key={c.slug} to={c.slug === "video" ? "/video" : `/category/${c.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${c.slug}`}
                  className="font-head font-semibold uc py-3 border-b border-gray-100 flex justify-between items-center">
                  <span>{c.name}</span><span className="font-hindi text-gray-400 text-sm normal-case tracking-normal">{c.name_hi}</span>
                </Link>
              ))}
              <Link to="/archive" onClick={() => setOpen(false)} className="font-head font-semibold uc py-3 border-b border-gray-100">Archive</Link>
              <Link to="/video" onClick={() => setOpen(false)} data-testid="mobile-nav-video-page" className="font-head font-semibold uc py-3 border-b border-gray-100">Video News</Link>
              <Link to="/about" onClick={() => setOpen(false)} className="font-head font-semibold uc py-3 border-b border-gray-100">About Us</Link>
              <Link to="/page/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-contact" className="font-head font-semibold uc py-3 border-b border-gray-100">Contact Us</Link>
              <Link to="/submit-tip" onClick={() => setOpen(false)} className="font-head font-semibold uc py-3 border-b border-gray-100">Submit News Tip</Link>
            </div>
            <div className="flex items-center gap-4 pt-5">
              <a href={SOCIAL.youtube.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.youtube.label} data-testid="mobile-youtube" className="flex items-center gap-2 font-semibold text-sm hover:text-r24-red"><Youtube size={20} /> YouTube</a>
              <a href={SOCIAL.instagram.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.instagram.label} data-testid="mobile-instagram" className="flex items-center gap-2 font-semibold text-sm hover:text-r24-red"><Instagram size={18} /> Instagram</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
